import { ReactNode, useEffect, useState } from 'react'
import { Box, SxProps, Theme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HomeIcon from '@mui/icons-material/Home'
import { getUsername } from 'Features/auth/auth'
import { FONT_SANS, SchedulerThemeScope, TOKENS } from './SchedulerDesign'

/**
 * Dark-teal brand bar for full-bleed Scheduler surfaces (see
 * `Scheduler/design_handoff_rules_and_constraints/scheduler/ui_kit_v3/App.jsx`
 * "top brand bar"). Replaces the shared Planner Header on the routes Shell
 * renders full-bleed for — carries the tenant name, user, and tenant-switch
 * entry point that the Header would otherwise have provided.
 */
export const SchedulerBrandBar = ({ trailing }: { trailing?: ReactNode }) => {
  const navigate = useNavigate()
  const tenantName = localStorage.getItem('tenantName') ?? ''
  const [userName, setUserName] = useState('')

  useEffect(() => {
    getUsername().then((name) => setUserName((name as string) ?? ''))
  }, [])

  const tenantSwitchEnabled =
    parseInt(localStorage.getItem('tenantsCount') as string) > 1

  return (
    <Box
      sx={{
        height: 44,
        background: TOKENS.brandTeal,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        flexShrink: 0,
      }}
    >
      <Box
        component="button"
        onClick={() => navigate('/plan-scheduler')}
        sx={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 0,
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '3px',
            background: TOKENS.brandBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ViewTimelineIcon sx={{ fontSize: 15, color: '#fff' }} />
        </Box>
        <Box sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
          Scheduler
        </Box>
      </Box>

      {trailing}

      <Box sx={{ flex: 1 }} />

      {tenantName && (
        <Box sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          {tenantName}
        </Box>
      )}
      {userName && (
        <Box sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
          {userName}
        </Box>
      )}
      {tenantSwitchEnabled && (
        <Box
          component="button"
          title="Switch tenant"
          onClick={() => navigate('/select-tenant')}
          sx={{
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'transparent',
            color: '#fff',
            borderRadius: '3px',
            px: 1,
            py: '2px',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { background: 'rgba(255,255,255,0.1)' },
          }}
        >
          Switch
        </Box>
      )}
    </Box>
  )
}

export type BreadcrumbItem = {
  label: string
  /** Clickable when set; the last crumb usually has none. */
  onClick?: () => void
}

/**
 * The 40px crumb bar under `SchedulerBrandBar` on full-bleed Scheduler pages:
 * back arrow · teal "plan-scheduler" home crumb · "/"-separated items. The
 * same bar the Factory view draws (`SchedulerInput.page.tsx`), shared so the
 * other Scheduler pages read the same way.
 */
export const SchedulerBreadcrumb = ({ items }: { items: BreadcrumbItem[] }) => {
  const navigate = useNavigate()
  const home = () => navigate('/plan-scheduler')
  return (
    <Box
      sx={{
        height: 40,
        flexShrink: 0,
        background: TOKENS.surface0,
        borderBottom: `1px solid ${TOKENS.line300}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
      }}
    >
      <Box
        component="button"
        title="Back to plan-scheduler home"
        onClick={home}
        sx={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          p: 0.5,
          borderRadius: '2px',
          color: TOKENS.ink500,
          '&:hover': { background: 'rgba(0,65,90,0.06)' },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box
        component="button"
        onClick={home}
        sx={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          p: 0,
          fontFamily: FONT_SANS,
          fontSize: 12.5,
          fontWeight: 600,
          color: TOKENS.brandTeal,
        }}
      >
        <HomeIcon sx={{ fontSize: 14, color: TOKENS.brandTeal }} />
        plan-scheduler
      </Box>
      {items.map((item, i) => {
        const last = i === items.length - 1
        return (
          <Box key={`${item.label}-${i}`} sx={{ display: 'contents' }}>
            <Box component="span" sx={{ color: TOKENS.ink300 }}>
              /
            </Box>
            <Box
              component={item.onClick ? 'button' : 'span'}
              onClick={item.onClick}
              title={item.label}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: item.onClick ? 'pointer' : 'default',
                p: 0,
                fontFamily: FONT_SANS,
                fontSize: 12.5,
                fontWeight: last ? 400 : 700,
                color: last ? TOKENS.ink500 : TOKENS.ink700,
                maxWidth: 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

/**
 * A full-bleed Scheduler page: brand bar, crumb bar, then the page body.
 *
 * Only the two bars sit inside `SchedulerThemeScope`. The body is left
 * unthemed on purpose: the scope also restyles every data grid inside it, and
 * pages such as System of records were built against the default grid look.
 * The route must also be listed in `Common/Layout`'s `isFullBleedRoute`, or
 * the Planner header and footer draw around it as well.
 */
export const SchedulerPageFrame = ({
  crumbs,
  bodySx,
  children,
}: {
  crumbs: BreadcrumbItem[]
  bodySx?: SxProps<Theme>
  children: ReactNode
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      background: TOKENS.surface50,
    }}
  >
    <SchedulerThemeScope sx={{ flexShrink: 0 }}>
      <SchedulerBrandBar />
      <SchedulerBreadcrumb items={crumbs} />
    </SchedulerThemeScope>
    <Box
      sx={[
        {
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        },
        ...(Array.isArray(bodySx) ? bodySx : [bodySx]),
      ]}
    >
      {children}
    </Box>
  </Box>
)
